/* eslint-disable no-underscore-dangle,no-param-reassign */
import { Logger } from 'vue-infra';
import { each, get, has, isNull } from 'lodash';
import ConversationsService from '@core/services/conversations/ConversationsService';
import ConversationBuilderService from '@core/services/conversations/ConversationBuilderService';
import { DATA_LOADING_STATES, RETRY_INTERVAL, WIDGET_LOGICAL_NAMES } from '@config/Consts';
import { getApiTimeParams, isMaxNumRetriesReached, isEventRelevant } from '@core/utils/utils';
import ConversationsModel from '@core/models/ConversationsModel';
import { Subscription } from '@core/common/Subscription';
import { ROOT_GETTER_PATHS } from '@core/store';

const logger = Logger.getLogger('conversations');

const _createSubscriptionPayload = (getters) => {
  logger.debug('Conversations::actions::_createSubscriptionPayload');

  return {
    filter: {
      timeframe: getters.getTimeFrameFilter,
      agentIds: getters.getAgentsFilter,
      groupIds: getters.getFilteredGroupsIds,
      status: getters.getStatusFilter,
      skills: getters.getSkillsFilter,
      liveIntentIds: getters.getLiveIntentsFilter,
    },
    sort: getters.getSort,
    paging: getters.getPaginationConfigForAPI,
    getters,
  };
};

const _getConversationsInterceptor = (getters, request) => { // TODO refactor ? what is this?
  const from = get(request, 'data.start.from');
  const to = get(request, 'data.start.to');
  const technicalEnd = get(request, 'data.technicalEnd.from');
  if (from && to && technicalEnd) {
    const apiTimeParams = getApiTimeParams(getters.getTimeFrameFilter);
    request.data.start.from = apiTimeParams.from;
    request.data.start.to = apiTimeParams.to;
    request.data.technicalEnd.from = apiTimeParams.lastUpdateFrom;
  }
  return request;
};

/**
 *
 * @param commit
 * @param getters
 * @param response - object with two properties: data and conversationsCount
 * @private
 */
const _onSubscriptionUpdateNotification = (commit, getters, response) => {
  logger.debug('Conversations::actions::_onSubscriptionUpdateNotification', response);
  const conversationsList = [];
  if (response) {
    let dataStatus;
    if (has(response, 'data') && !isNull(response.data)) {
      dataStatus = DATA_LOADING_STATES.SUCCESS;
      each(response.data, (data) => {
        if (data && data instanceof ConversationsModel) {
          conversationsList.push(data);
        }
      });
      commit('setDataStatus', DATA_LOADING_STATES.SUCCESS);
    } else if (getters.dataHasBeenFetched) {
      // See incident INC0045645 - if status 200 and body is null,
      // it occurs only in a very few cases when the request is being canceled.
      // in this case we do not want to display the red frame
      // dataStatus = DATA_LOADING_STATES.OBSOLETE;
    } else {
      dataStatus = DATA_LOADING_STATES.ERROR;
    }
    commit('setConversations', { dataStatus, conversationsList });
    if (has(response, 'conversationsCount')) {
      commit('setPagination', { key: 'totalPages', value: response.conversationsCount });
    }
  }
};

const _onSubscriptionError = ({ commit, getters, rootGetters }, resourceName, err) => {
  if (err && !err.cancelled) {
    logger.error('Conversations::actions::_onSubscriptionError', JSON.stringify({
      resourceName,
      err,
    }));
    commit('setDataStatus', DATA_LOADING_STATES.ERROR);
    if (isMaxNumRetriesReached(err)) {
      // eslint-disable-next-line no-use-before-define
      setTimeout(() => updateSubscription({ commit, getters, rootGetters }), RETRY_INTERVAL);
    }
  }
};

const updateSubscription = async ({ commit, getters, rootGetters }, data) => {
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.CONVERSATIONS)) {
    logger.debug('Conversations::no permission to update the conversation widget subscription');
    return;
  }
  const DEFAULT_UPDATE_SUBSCRIPTION_DATA = {
    resetPageNumber: true,
  };
  const dataParams = Object.assign(DEFAULT_UPDATE_SUBSCRIPTION_DATA, data);
  const subscription = ConversationsService.getSubscription({
    interceptor: _getConversationsInterceptor.bind(this, getters),
  });
  if (dataParams.resetPageNumber) {
    commit('setPagination', { key: 'currentPage', value: 1 });
  }
  const subscriptionPayload = _createSubscriptionPayload(getters);
  try {
    await subscription.update(subscriptionPayload);
  } catch (ex) {
    logger.error('cannot update Conversations list subscription', 'start', ex);
  }
};

const startSubscription = async ({ commit, getters, rootGetters }) => {
  logger.debug('Conversations::actions::startSubscription');
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.CONVERSATIONS)) {
    logger.debug('Shift Status::no permission to start the conversation widget subscription');
    return;
  }
  commit('setDataStatus', DATA_LOADING_STATES.LOADING);
  const subscription = ConversationsService.getSubscription({
    interceptor: _getConversationsInterceptor.bind(this, getters),
  });
  subscription.clearAllEventListeners();
  subscription.on(Subscription.EVENTS.onSubscriptionUpdateNotification,
    _onSubscriptionUpdateNotification.bind(this, commit, getters),
    'conversationSubscriptionUpdateNotificationSuccess');
  subscription.on(Subscription.EVENTS.onSubscriptionError,
    _onSubscriptionError.bind(this, { commit, getters, rootGetters }, subscription.resourceName),
    'conversationSubscriptionUpdateNotificationError');
  const subscriptionPayload = _createSubscriptionPayload(getters);
  try {
    logger.debug('Conversations::actions::before executing start subscription');
    await subscription.start(subscriptionPayload);
  } catch (ex) {
    logger.error('cannot start Conversations list subscription', 'start', ex);
  }
};

const stopSubscription = async ({ commit, getters, rootGetters }) => {
  logger.debug('Conversations::actions::stopSubscription');
  // eslint-disable-next-line max-len
  if (!rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GET_WIDGET_PERMISSION](WIDGET_LOGICAL_NAMES.CONVERSATIONS)) {
    logger.debug('Shift Status::no permission to stop the conversation widget subscription');
    return;
  }
  const subscription = ConversationsService.getSubscription({
    interceptor: _getConversationsInterceptor.bind(this, getters),
  });
  subscription.clearAllEventListeners();
  try {
    logger.debug('Conversations::actions::before executing stop subscription');
    await subscription.stop();
    commit('setDataStatus', DATA_LOADING_STATES.NONE);
  } catch (ex) {
    logger.error('cannot stop Conversations list subscription', 'stop', ex);
  }
};

const changeAgentNameFilter = ({ commit, getters, rootGetters }, agentsFilterList) => {
  commit('setAgentsFilter', agentsFilterList);
  return updateSubscription({ commit, getters, rootGetters });
};

const changeStatusFilter = ({ commit, getters, rootGetters }, statusFilterList) => {
  commit('setStatusFilter', statusFilterList);
  return updateSubscription({ commit, getters, rootGetters });
};

const changeSort = ({ commit, getters, rootGetters }, sort) => {
  commit('setSort', sort);
  return updateSubscription({ commit, getters, rootGetters });
};

const fetchLiveIntents = async ({ commit, getters }) => {
  if (getters.getLiveIntentsDataStatus === DATA_LOADING_STATES.NONE) {
    commit('setLiveIntentsDataStatus', DATA_LOADING_STATES.LOADING);
    ConversationBuilderService.getLiveIntents()
      .then((result) => {
        commit('setLiveIntents', {
          intents: result,
          dataStatus: DATA_LOADING_STATES.SUCCESS,
        });
      })
      .catch((ex) => {
        commit('setLiveIntentsDataStatus', DATA_LOADING_STATES.ERROR);
        logger.error('cannot fetch live intents', ex);
      });
  }
};

const changeLiveIntentsFilter = ({ commit, getters, rootGetters }, liveIntentsFilter) => {
  commit('setLiveIntentsFilter', liveIntentsFilter);
  return updateSubscription({ commit, getters, rootGetters });
};

const applyPresetValues = ({ commit }, preset) => {
  commit('setPresetValues', preset);
};

const handleStartSubscription = ({ commit, getters, rootGetters }, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.CONVERSATIONS, widgets)) {
    startSubscription({ commit, getters, rootGetters });
  }
};
const handleStopSubscription = ({ commit, getters, rootGetters }, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.CONVERSATIONS, widgets)) {
    stopSubscription({ commit, getters, rootGetters });
  }
};
const handleUpdateSubscription = ({ commit, getters, rootGetters }, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.CONVERSATIONS, widgets)) {
    updateSubscription({ commit, getters, rootGetters });
  }
};
const handleShowWidgets = ({ commit, getters, rootGetters }, widgets) => {
  if (isEventRelevant(WIDGET_LOGICAL_NAMES.CONVERSATIONS, widgets)) {
    startSubscription({ commit, getters, rootGetters });
  } else {
    stopSubscription({ commit, getters, rootGetters });
  }
};

export default {
  changeAgentNameFilter,
  changeStatusFilter,
  changeSort,
  fetchLiveIntents,
  changeLiveIntentsFilter,
  applyPresetValues,
  handleStartSubscription,
  handleStopSubscription,
  handleUpdateSubscription,
  handleShowWidgets,
  updateSubscription, // needed for pagination
};
